import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Send, Trash2, Mail, Bell, AlertCircle, RefreshCw, ChevronDown, ChevronRight } from "lucide-react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";

const CHANNELS = [
  { id: "popup", label: "Popup", icon: AlertCircle },
  { id: "notification", label: "Inbox Notification", icon: Bell },
  { id: "email", label: "Email (via Affiliate Email gateway)", icon: Mail },
];

export default function AffiliateAnnouncements() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    title: "",
    body: "",
    audience: "active",
    channels: ["notification"],
    cta_label: "",
    cta_url: "",
  });
  const [creating, setCreating] = useState(false);
  const [expand, setExpand] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/aff-announcements");
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleChannel = (id) => {
    setForm((s) => {
      const set = new Set(s.channels);
      if (set.has(id)) set.delete(id); else set.add(id);
      return { ...s, channels: Array.from(set) };
    });
  };

  const submit = async () => {
    if (!form.title || !form.body) return toast.error("Title and body are required");
    if (form.channels.length === 0) return toast.error("Select at least one channel");
    setCreating(true);
    try {
      const r = await api.post("/admin/aff-announcements", form);
      toast.success(`Sent to ${r.data?.delivered || 0} affiliates`);
      setForm({ title: "", body: "", audience: "active", channels: ["notification"], cta_label: "", cta_url: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setCreating(false); }
  };

  const openDetail = async (id) => {
    if (expand === id) { setExpand(null); setDetail(null); return; }
    setExpand(id); setDetail(null);
    try {
      const r = await api.get(`/admin/aff-announcements/${id}`);
      setDetail(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      await api.delete(`/admin/aff-announcements/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-announcements-page">
      <PageHeader
        eyebrow="AFFILIATE"
        title="Announcements"
        subtitle="Broadcast popup, notification, and email messages to affiliates."
      />

      {/* Compose */}
      <div className="rounded-xl p-5 bg-[#0f1a2e] border border-white/10 mb-6">
        <div className="text-[10px] uppercase tracking-[0.28em] text-emerald-300 font-bold mb-3">Compose</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-white/55 font-bold">Title</label>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-1 w-full h-11 px-3 rounded-lg bg-black/30 border border-white/10 text-white text-[14px]"
              placeholder="Eid Bonus Campaign Started"
              data-testid="ann-title"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-white/55 font-bold">Audience</label>
            <select
              value={form.audience}
              onChange={(e) => setForm({ ...form, audience: e.target.value })}
              className="mt-1 w-full h-11 px-3 rounded-lg bg-black/30 border border-white/10 text-white text-[14px]"
              data-testid="ann-audience"
            >
              <option value="all">All affiliates</option>
              <option value="active">Active only</option>
            </select>
          </div>
        </div>
        <div className="mt-3">
          <label className="text-[10px] uppercase tracking-[0.18em] text-white/55 font-bold">Body</label>
          <textarea
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            rows={5}
            className="mt-1 w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white text-[14px] resize-y"
            placeholder="Write your message — supports plain text, line breaks preserved."
            data-testid="ann-body"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-white/55 font-bold">CTA Label (optional)</label>
            <input
              value={form.cta_label}
              onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
              className="mt-1 w-full h-11 px-3 rounded-lg bg-black/30 border border-white/10 text-white text-[14px]"
              placeholder="View Campaign"
              data-testid="ann-cta-label"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-white/55 font-bold">CTA URL (optional)</label>
            <input
              value={form.cta_url}
              onChange={(e) => setForm({ ...form, cta_url: e.target.value })}
              className="mt-1 w-full h-11 px-3 rounded-lg bg-black/30 border border-white/10 text-white text-[14px]"
              placeholder="https://maxlive88.com/eid-bonus"
              data-testid="ann-cta-url"
            />
          </div>
        </div>
        <div className="mt-3">
          <label className="text-[10px] uppercase tracking-[0.18em] text-white/55 font-bold">Channels</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {CHANNELS.map((c) => {
              const Icon = c.icon;
              const active = form.channels.includes(c.id);
              return (
                <button
                  key={c.id}
                  onClick={() => toggleChannel(c.id)}
                  className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-[12px] font-bold transition"
                  style={{
                    background: active ? "rgba(34,192,115,0.18)" : "rgba(255,255,255,0.04)",
                    border: `1px solid ${active ? "rgba(34,192,115,0.45)" : "rgba(255,255,255,0.08)"}`,
                    color: active ? "#a7f3d0" : "#cbd5e1",
                  }}
                  data-testid={`ann-channel-${c.id}`}
                >
                  <Icon size={12} /> {c.label}
                </button>
              );
            })}
          </div>
        </div>
        <button
          onClick={submit}
          disabled={creating}
          className="mt-4 inline-flex items-center gap-2 h-11 px-6 rounded-xl text-[13px] font-bold"
          style={{ background: "linear-gradient(135deg,#22c073,#16a34a)", color: "#0a070f" }}
          data-testid="ann-send"
        >
          <Send size={14} /> {creating ? "Sending…" : "Send Announcement"}
        </button>
      </div>

      {/* History */}
      <div className="rounded-xl bg-[#0f1a2e] border border-white/10 overflow-hidden">
        <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
          <Megaphone size={14} className="text-emerald-300" />
          <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-300 font-bold">History</div>
          <button onClick={load} className="ml-auto px-2 h-8 rounded-lg bg-white/5 text-white text-[11px] inline-flex items-center gap-1"><RefreshCw size={11} /> Refresh</button>
        </div>
        {loading ? (
          <div className="p-8 text-center text-white/55">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-white/55">No announcements sent yet.</div>
        ) : (
          <table className="w-full text-[12.5px]">
            <thead><tr className="bg-white/[0.03] text-emerald-300">
              <th className="text-left p-3" style={{ width: 28 }}></th>
              <th className="text-left p-3">Title</th>
              <th className="text-left p-3">Channels</th>
              <th className="text-right p-3">Targets</th>
              <th className="text-right p-3">Email Sent / Failed</th>
              <th className="text-right p-3">Sent</th>
              <th className="text-center p-3">Action</th>
            </tr></thead>
            <tbody>
              {items.map((a) => (
                <>
                  <tr key={a.id} className="border-t border-white/5">
                    <td className="p-3">
                      <button onClick={() => openDetail(a.id)} className="text-white/55 hover:text-white" data-testid={`ann-expand-${a.id}`}>
                        {expand === a.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </button>
                    </td>
                    <td className="p-3 text-white">{a.title}</td>
                    <td className="p-3 text-white/70 text-[11px]">{(a.channels || []).join(", ")}</td>
                    <td className="p-3 text-right text-white">{a.target_count}</td>
                    <td className="p-3 text-right text-white">{a.email_sent || 0} / {a.email_failed || 0}</td>
                    <td className="p-3 text-right text-white/55 text-[11px]">{(a.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="p-3 text-center">
                      <button onClick={() => remove(a.id)} className="p-1.5 rounded-md hover:bg-red-500/10 text-red-400" data-testid={`ann-delete-${a.id}`}><Trash2 size={13} /></button>
                    </td>
                  </tr>
                  {expand === a.id && detail && (
                    <tr key={a.id + "-d"} className="border-t border-white/5 bg-black/30">
                      <td colSpan={7} className="p-5">
                        <div className="text-[11.5px] text-white whitespace-pre-wrap">{detail.announcement?.body}</div>
                        <div className="grid grid-cols-4 gap-2 mt-3 text-[11px]">
                          <div className="p-2 rounded bg-white/5"><div className="text-white/55">Popup dismissed</div><div className="text-white font-bold">{detail.stats?.popup_dismissed} / {detail.stats?.total}</div></div>
                          <div className="p-2 rounded bg-white/5"><div className="text-white/55">Notif read</div><div className="text-white font-bold">{detail.stats?.notification_read} / {detail.stats?.total}</div></div>
                          <div className="p-2 rounded bg-white/5"><div className="text-white/55">Email sent</div><div className="text-emerald-300 font-bold">{detail.stats?.email_sent}</div></div>
                          <div className="p-2 rounded bg-white/5"><div className="text-white/55">Email failed</div><div className="text-red-400 font-bold">{detail.stats?.email_failed}</div></div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
